import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { Address } from 'src/app/shared/models/address';
import { ConnectorType } from 'src/app/shared/enums/connector-type.enum';
import { DefaultAccessGroup } from '../access-groups/access-group.model';

export const DYNAMIC_PRICE_POLICY_NET_AREA_MESSAGE =
  'Dynamiske prispolitikker kan ikke anvendes, da lokationen ikke har en gyldig netområdekode. Vælg en fast prispolitik.';

export interface LocationBase {
  identifier: string;
  name: string;
  departmentName: string;
  organizationId: number;
  organizationName: string;
  appVisibilityStatus: AppVisibilityStatus;
  hasDefaultPricePolicy: boolean;
  latitude: number;
  longitude: number;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface ChargingStationLocationDisplay extends ChargingStationLocation {}

export interface ChargingStationLocation {
  identifier: string;
  name: string;
  departmentName: string;
  organizationId: number;
  organizationName: string;
  appVisibilityStatus: AppVisibilityStatus;
  hasDefaultPricePolicy: boolean;
  latitude: number;
  longitude: number;
  road: string;
  number: number;
  letter: string;
  floor: string;
  door: string;
  postalCode: string;
  city: string;
  departmentId: number;
  address: Address;
  checked: boolean;
  showDangerBadge: boolean;
  dangerBadgeMessage: string;
  highlight?: boolean;
  restricted: boolean;
  operatingDate: Date;
  anyConnectorWithoutPricePolicy: boolean;
  anyConnectorWithoutAccessGroup: boolean;
  canHaveDynamicPricePolicy: boolean;
  hasOkPowerMeter?: boolean;
}

export interface ChargingStationRowNavigationData {
  organizationId: number;
}

export interface ChargingStationLocationDetails {
  chargingStationLocationRelationId: number;
  kwhCompensationAgreementNumber: string;
  appLocationText?: string;
  conceptInstallationNumber?: number;
  chargingStationLocationIdentifier: string;
  chargingStationLocationName: string;
  category: LocationCategory;
  meteringPointIdentifiers?: string[] | null;
  defaultPricePolicy?: { id: number; name: string } | null;
  defaultAccessGroups?: DefaultAccessGroup[];
  restricted: boolean;
  isLargeVehicleAccessible: boolean;
  facilities: LocationFacility[];
  product: LocationProduct;
  kickBack?: { id: number; name: string } | null;
  organizationId: number;
  latitude: number;
  longitude: number;
  road: string;
  number: number;
  letter?: string;
  floor?: string;
  door?: string;
  postalCode: string;
  city: string;
  appVisibilityStatus: AppVisibilityStatus;
  locationAppVisibility: LocationAppVisibility;
  hasOkPowerMeter?: boolean;
  hasPowerPriceExclusion?: boolean;
  darId?: string;
  // Optional so the portal still works if it is released before the backend that returns it; falls back to the address lookup.
  canHaveDynamicPricePolicy?: boolean;
}

export interface SimpleConnector {
  connectorId: number;
  stationNumber: number | null;
  connectorIdentifier: string;
  chargingStationIdentifier: string;
  connectorType: string;
  globalEvseId: string;
  power: number;
}

export interface UpdateChargingStationLocationRequest {
  address: UpdateChargingStationLocationAddressRequest;
  facilities: LocationFacility[];
  product: LocationProduct | null;
  kickback_id: number | null;
  relation: UpdateChargingStationLocationRelationRequest;
}

export interface UpdateChargingStationLocationAddressRequest {
  road: string;
  number: number;
  letter: string | null;
  floor: string | null;
  door: string | null;
  postal_code: string;
  city: string;
  latitude: number;
  longitude: number;
  dar_id: string | null;
}

export interface UpdateChargingStationLocationRelationRequest {
  kwh_compensation_agreement_number: string | null;
  restricted: boolean;
  category: LocationCategory;
  concept_installation_number: number | null;
  app_visibility_status: AppVisibilityStatus;
  location_app_visibility: LocationAppVisibility;
  app_location_text: string | null;
  metering_point_identifiers: string[];
  is_large_vehicle_accessible: boolean;
  has_ok_power_meter: boolean;
  price_policy_id: number | null;
  has_power_price_exclusion: boolean;
}

export class ChargingStationLocationDefaultsRequest {
  constructor(pricePolicyId: number, accessGroupIdentifiers: string[]) {
    this.pricePolicyId = pricePolicyId;
    this.accessGroupIdentifiers = accessGroupIdentifiers;
  }
  pricePolicyId: number;
  accessGroupIdentifiers: string[];
}

export enum LocationCategory {
  Workplace = 'Workplace',
  HousingAssociation = 'HousingAssociation',
  OnTheMove = 'OnTheMove',
  Public = 'Public',
  Depot = 'Depot',
  ETruck = 'ETruck',
}

export enum AppVisibilityStatus {
  Hidden = 'Hidden',
  Coming = 'Coming',
  Restrictions = 'Restrictions',
  Visible = 'Visible',
}

export class CreateChargingStationLocation {
  organizationId: number;
  departmentId: number;
  pricePolicyId: number;
  name: string;
  customerNumbers: Array<number>;
  latitude: number;
  longitude: number;
  restricted: boolean;
  address: CreateChargingStationLocationAddressForm;
  RelationInfo: CreateChargingStationLocationExtraInformationForm;
  facilities: LocationFacility[];
  product: LocationProduct;
  kickbackId?: number;
}
export class CreateChargingStationLocationAddressForm {
  street: string;
  houseNumber: number;
  houseLetter: string;
  zipCode: string;
  town: string;
  door: string | null;
  floor: string | null;
  darId?: string | null;
}

export class CreateChargingStationLocationExtraInformationForm {
  category: LocationCategory;
  locationAppVisibilityStatus: AppVisibilityStatus;
  appLocationText: string;
  conceptInstallationNumber: number;
  agreementNumber: string;
  meteringPointIdentifiers: string[];
  hasOkPowerMeter: boolean;
  hasPowerPriceExclusion: boolean;
  accessGroupIdentifiers: Array<string>;
  isLargeVehicleAccessible: boolean;
  locationAppVisibility: LocationAppVisibility;
}

export class CreateCharginStationLocationExtraInfo {}

export class ChargingStationLocationSearchEvent {
  constructor(paging: PageEvent) {
    this.paging = paging;
    this.sorting = null;
    this.hasAccessGroup = false;
  }
  organizationId?: number;
  oIds?: number[];
  departmentId?: number;
  pricePolicyId?: number;
  priceRegulationIdentifier?: string;
  accessGroupIdentifier?: string;
  locationsWithErrorsInSetup?: boolean;
  query?: string | null;
  chargingStationLocationIdentifier?: string;
  placeholderIdentifier?: string;
  paging: PageEvent;
  sorting: Sort | null;
  hasAccessGroup: boolean;
}

export interface ChargingStationLocationConnector {
  connectorIdentifier: string;
  stationNumber: number;
}

export interface GetConnectorsOnLocationResponse {
  connectors: Array<ChargingStationLocationConnector>;
}

export interface ConnectorLocationResponse {
  chargingStationLocationIdentifier: string;
  name: string;
  address: string;
  departmentName: string;
  connectors: ConnectorResponse[];
  selectedCount: number;
  allSelected: boolean;
  someSelected: boolean;
}
export interface ConnectorResponse {
  connectorIdentifier: string;
  stationNumber: number;
  connectorId: number;
  chargingStationIdentifier: string;
  isSelected: boolean;
  power: number;
  connectorType: ConnectorType;
  pricePolicyName: string;
  consumerPricePerKwh: number;
  priceType: PriceType;
  isAttachedToDifferentTerminal: boolean;
}

export enum PriceType {
  Fixed = 'Fixed',
  Dynamic = 'Dynamic',
}

export class ConnectorSearchEvent {
  organizationId?: number;
  query?: string;
}

export interface ConsumptionResponse {
  organizationId: number;
  organizationName: string;
  departmentId: number;
  departmentName: string;
  idTagName: string | null;
  idTagPrintedId: number | null;
  stationNumber: number | null;
  consumptionLocation: TransactionLocation;
  cabelStart: string;
  cabelStop: string | null;
  chargingStart: string;
  chargingStop: string;
  consumption: number;
}
export interface TransactionLocation {
  name: string | null;
  road: string | null;
  number: number | null;
  letter: string | null;
  postalCode: string | null;
  city: string | null;
}

export interface FirestoreConnectorInfo {
  firestoreIdentifier: string;
  chargingStationLocationIdentifier: string;
  chargingStationIdentifier: string;
  connectorNumber: number;
  connectorStatus: ConnectorStatus;
  priceId: number;
  pricePerKwhInOere: number;
  hasLimitedAccess: boolean;
}

export enum FirestoreStatus {
  Unknown = 0,
  Discrepancy = 1,
  Equal = 2,
}

export enum ConnectorStatus {
  Unknown = 0,
  Available = 1,
  Unavailable = 2,
  Charging = 3,
  Faulted = 4,
}

export interface ChargingStationLocationMinimalDto {
  identifier: string;
  name: string;
  road: string;
  number: string;
  letter: string;
  postalCode: string;
  city: string;
}

export interface UpdateAccessGroupsOnChargingStationLocationConnectorsRequest {
  chargingStationLocationIdentifier: string;
  accessGroupIdentifiers: string[];
}
export interface ChargingTransaction {
  chargingToken: string;
  organizationName: string | null;
  organizationLogo: string | null;
  organizationCompactLogo: string | null;
  organzationType: string | null;
  departmentName: string | null;
  accessGroupName: string | null;
  locationName: string | null;
  locationType: string | null;
  road: string | null;
  number: string | null;
  postalCode: string | null;
  city: string | null;
  latitude: number;
  longitude: number;
  chargingStationIdentifier: string | null;
  stationNumber: string | null;
  created: Date | null;
  chargingStart: Date | null;
  consumption: number | null;
  chargeTime: number | null;
  isCharging: boolean;
  origin: string | null;
  paymentMethod: string | null;
  pricePerUnit: number | null;
  liveDoc?: {
    id: string;
    chargeInWh: number;
    powerUpdated: Date;
    totalConsumptionInWh: number;
    powerInW: number;
    fireStoreDocLoaded: boolean;
    // any other fields your Firestore doc contains…
  };
}

export enum LocationFacility {
  Hotel = 'Hotel',
  Restaurant = 'Restaurant',
  Cafe = 'Cafe',
  Mall = 'Mall',
  Supermarket = 'Supermarket',
  Sport = 'Sport',
  RecreationArea = 'RecreationArea',
  Nature = 'Nature',
  Museum = 'Museum',
  BikeSharing = 'BikeSharing',
  BusStop = 'BusStop',
  TaxiStand = 'TaxiStand',
  TramStop = 'TramStop',
  MetroStation = 'MetroStation',
  TrainStation = 'TrainStation',
  Airport = 'Airport',
  ParkingLot = 'ParkingLot',
  CarpoolParking = 'CarpoolParking',
  Wifi = 'Wifi',
  FuelStation = 'FuelStation',
}

export enum LocationProduct {
  OkTotal = 'OkTotal',
  Eje = 'Eje',
  LejeMedOkMaaler = 'LejeMedOkMaaler',
  LejeUdenOkMaaler = 'LejeUdenOkMaaler',
  KWhMedOkMaaler = 'KWhMedOkMaaler',
  KWhUdenOkMaaler = 'KWhUdenOkMaaler',
  Haandteringsgebyr = 'Haandteringsgebyr',
  Kickback = 'Kickback',
  DAB = 'DAB',
}

export enum LocationAppVisibility {
  Both = 'Both',
  Commercial = 'Commercial',
  Public = 'Public',
}
